import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { ModalProps } from '@/components/utilities/modal/modal';
import SlideIn from '@/components/utilities/slide-in/slide-in';
import useQueryStoreSharedFavorites from '@/integrations/offers/favorites/share/store-hotels-with-offers/store-hotels-with-offers.hooks';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCopyToClipboard from '@/libraries/hooks/use-copy-to-clipboard';
import styles from './favorites-slide-in.module.scss';

export interface FavoritesSlideInProps extends Omit<ModalProps, 'children'> {
  /** The body text of the composed email */
  emailBody?: string;
  /** The subject text of the composed email */
  emailSubject?: string;
  /** The headline of the favorites slide in */
  headline?: string;
  /** The text of the favorites slide in */
  text?: string;
}

const FavoritesSlideIn = ({
  emailBody,
  emailSubject,
  headline,
  isOpen,
  text,
  setIsOpen,
  ...rest
}: FavoritesSlideInProps) => {
  const { t } = useI18n();
  const pathname = usePathname();
  const { favoriteOffers } = useFavoritesContext();
  const { copyToClipboard } = useCopyToClipboard();

  const [shareUrl, setShareUrl] = useState('');
  const [favoritesKey, setFavoritesKey] = useState('');
  const [favoriteOfferIds, setFavoriteOfferIds] = useState('');
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const [isEmailClicked, setIsEmailClicked] = useState(false);
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [isSameFavorites, setIsSameFavorites] = useState(true);

  const {
    data: dataStoreSharedFavorites,
    isFetching: isFetchingStoreSharedFavorites,
  } = useQueryStoreSharedFavorites(
    { favoriteIds: favoriteOfferIds },
    { enabled: !isSameFavorites },
  );

  const handleShareViaEmail = (url: string) => {
    if (typeof window === 'undefined') return;

    const subject = encodeURIComponent(emailSubject ? emailSubject : '');
    const body = encodeURIComponent(emailBody ? `${emailBody} ${url}` : url);

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleClickEmailLink = () => {
    if (!isEmailClicked) return;

    if (!shareUrl) return;

    handleShareViaEmail(shareUrl);
    setIsEmailClicked(false);
  };

  const handleClickCopyLink = () => {
    if (!isCopyClicked) return;

    if (!shareUrl) return;

    void copyToClipboard(shareUrl);

    setTimeout(() => {
      setIsCopyClicked(false);
    }, 1000);
  };

  useEffect(() => {
    if (!favoritesKey || typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);

    searchParams!.set('key', favoritesKey);
    const shareLink = `${window.location.origin}${window.location.pathname}?${searchParams!.toString()}`;

    setShareUrl(shareLink);

    if (isCopyClicked) {
      void copyToClipboard(shareLink);

      setTimeout(() => {
        setIsCopyClicked(false);
      }, 1000);
    }

    if (isEmailClicked) {
      handleShareViaEmail(shareLink);
      setIsEmailClicked(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritesKey, pathname]);

  useEffect(() => {
    if (!dataStoreSharedFavorites) return;

    setFavoritesKey(dataStoreSharedFavorites.guid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataStoreSharedFavorites]);

  useEffect(() => {
    if (isEmailClicked) return;

    if (!isOpen) return;

    if (isFetchingStoreSharedFavorites) {
      setShouldShowLoader(true);
    } else {
      setTimeout(() => {
        setShouldShowLoader(false);
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingStoreSharedFavorites]);

  useEffect(() => {
    if (!isOpen) return;

    const isSameOffers = favoriteOffers.join(',') === favoriteOfferIds;

    if (isSameOffers) {
      setIsSameFavorites(true);
      handleClickCopyLink();
      handleClickEmailLink();
    } else {
      setIsSameFavorites(false);
      setFavoriteOfferIds(favoriteOffers.join(','));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCopyClicked, isEmailClicked]);

  return (
    <SlideIn
      {...rest}
      className={styles.component}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div className={styles.content}>
        <button className={styles.close} onClick={() => setIsOpen?.(false)}>
          <Icon name="Close" />
        </button>

        {(headline || text) && (
          <div className={styles.headlineWrapper}>
            {headline && (
              <Typography className={styles.headline} tagStyle="headingMedium">
                {headline}
              </Typography>
            )}

            {text && <RichText className={styles.text} text={text} />}
          </div>
        )}

        <div className={styles.divider} />

        {shouldShowLoader ? (
          <SkeletonLoader borderRadius="4px" height="24px" width="25%" />
        ) : (
          <Link
            className={classNames(styles.copyContent, {
              [styles.isCopyClicked]: isCopyClicked,
            })}
            onClick={() => setIsCopyClicked(true)}
          >
            <div className={classNames(styles.copy, styles.copyLink)}>
              <Icon name="Copy" size={16} />

              <Typography tagStyle="bodyMedium">
                {t('items.favorites.copyLink')}
              </Typography>
            </div>

            <div className={classNames(styles.copy, styles.copyLinkMessage)}>
              <Icon name="CheckMark" size={16} />

              <Typography tagStyle="bodyMedium">
                {t('items.favorites.copyLinkMessage')}
              </Typography>
            </div>
          </Link>
        )}

        <div className={styles.divider} />

        <Link className={styles.share} onClick={() => setIsEmailClicked(true)}>
          <Icon name="Email" size={16} />

          <Typography tagStyle="bodyMedium">
            {t('items.favorites.shareViaEmail')}
          </Typography>
        </Link>

        <div className={styles.divider} />
      </div>
    </SlideIn>
  );
};

FavoritesSlideIn.displayName = 'FavoritesSlideIn';

export default FavoritesSlideIn;
